import { Navigation } from '../../components/Navigation';
import { Shield, Check, Star, TrendingUp, Award, ArrowRight, Upload, Camera } from 'lucide-react';

export function VerifiedBadge() {
  const verificationSteps = [
    {
      icon: Upload,
      title: 'Upload ID',
      description: 'Government-issued ID or passport',
      time: '30 seconds',
    },
    {
      icon: Camera,
      title: 'Quick Selfie',
      description: 'Secure identity verification',
      time: '30 seconds',
    },
    {
      icon: Award,
      title: 'Skill Proof',
      description: 'Optional: Add certifications',
      time: '2 minutes',
    },
    {
      icon: Star,
      title: 'Get Verified',
      description: 'Badge appears on your profile',
      time: 'Instant',
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: '3x More Interview Requests',
      description: 'Verified profiles get significantly more employer attention',
      stat: '+200%',
    },
    {
      icon: Star,
      title: 'Priority in Search Results',
      description: 'Appear higher in employer searches and recommendations',
      stat: 'Top 10%',
    },
    {
      icon: Shield,
      title: 'Build Trust Instantly',
      description: 'Show employers you are a serious, verified candidate',
      stat: '95% Trust',
    },
  ];

  const badgeTypes = [
    {
      name: 'Identity Verified',
      icon: Shield,
      description: 'Confirmed identity via government ID',
      color: 'cyan',
    },
    {
      name: 'Skill Certified',
      icon: Award,
      description: 'Verified skills and qualifications',
      color: 'blue',
    },
    {
      name: 'Job Ready',
      icon: Check,
      description: 'Completed profile with references',
      color: 'green',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-100 to-blue-100 px-4 py-2 rounded-full mb-6">
            <Shield className="w-5 h-5 text-cyan-600" />
            <span className="text-sm font-semibold text-cyan-900">Professional Verification</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Get Verified
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Stand out instantly — get verified. 3x more interview requests with professional verification.
          </p>

          <div className="inline-flex items-baseline gap-2 mb-6">
            <span className="text-5xl font-bold text-gray-900">$4.99</span>
            <span className="text-gray-600">one-time</span>
          </div>

          <button className="px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl text-lg inline-flex items-center gap-2">
            Get Verified Now
            <ArrowRight className="w-5 h-5" />
          </button>

          <p className="text-sm text-gray-500 mt-4">Takes less than 3 minutes • Lifetime verification</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <div
                key={benefit.title}
                className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-cyan-300 transition-all hover:shadow-lg text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl mb-4">
                  <Icon className="w-8 h-8 text-white" />
                </div>

                <div className="text-4xl font-bold text-cyan-600 mb-2">
                  {benefit.stat}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            How Verification Works
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {verificationSteps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="bg-white rounded-xl p-6 border-2 border-gray-200 relative"
                >
                  {idx < verificationSteps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gray-300" />
                  )}

                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {step.description}
                  </p>
                  <div className="text-xs font-semibold text-cyan-600">
                    {step.time}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Verification Badges
          </h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {badgeTypes.map((badge) => {
              const Icon = badge.icon;
              return (
                <div
                  key={badge.name}
                  className="bg-white rounded-xl p-6 border-2 border-gray-200 text-center"
                >
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${
                      badge.color === 'cyan'
                        ? 'from-cyan-500 to-blue-500'
                        : badge.color === 'blue'
                        ? 'from-blue-500 to-cyan-500'
                        : 'from-green-500 to-emerald-500'
                    } rounded-full mb-4`}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {badge.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {badge.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 mb-16 text-white">
          <h2 className="text-3xl font-bold mb-6 text-center">
            Your Data is Secure
          </h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Shield className="w-10 h-10 mb-4 text-cyan-400" />
              <h3 className="text-xl font-bold mb-2">Bank-Level Encryption</h3>
              <p className="text-white/80 text-sm">
                Your verification documents are encrypted and never shared with employers or third parties
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Check className="w-10 h-10 mb-4 text-green-400" />
              <h3 className="text-xl font-bold mb-2">Instant Verification</h3>
              <p className="text-white/80 text-sm">
                Automated verification process means you're verified in minutes, not days
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Star className="w-10 h-10 mb-4 text-amber-400" />
              <h3 className="text-xl font-bold mb-2">Lifetime Badge</h3>
              <p className="text-white/80 text-sm">
                Once verified, your badge is permanent. No renewal fees or recurring charges
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <Award className="w-10 h-10 mb-4 text-purple-400" />
              <h3 className="text-xl font-bold mb-2">Optional Skills</h3>
              <p className="text-white/80 text-sm">
                Add certifications and skills to strengthen your profile even further
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              What Employers See
            </h3>

            <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-gray-900">John Smith</span>
                    <div className="flex items-center gap-1 bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full text-xs font-bold">
                      <Shield className="w-3 h-3" />
                      Verified
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Customer Service Professional</p>
                </div>
              </div>

              <p className="text-xs text-gray-500 bg-white rounded-lg p-3 border border-gray-200">
                This candidate has completed identity verification and maintains a trusted, professional profile.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Shows up with verified badge in search</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Ranked higher than unverified profiles</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">Builds instant credibility and trust</span>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Why Get Verified?
            </h3>

            <div className="space-y-4">
              <div>
                <div className="text-3xl font-bold mb-1">3x</div>
                <p className="text-white/90 text-sm">
                  More interview requests compared to unverified profiles
                </p>
              </div>

              <div>
                <div className="text-3xl font-bold mb-1">Top 10%</div>
                <p className="text-white/90 text-sm">
                  Priority placement in employer searches and recommendations
                </p>
              </div>

              <div>
                <div className="text-3xl font-bold mb-1">95%</div>
                <p className="text-white/90 text-sm">
                  Of employers prefer verified candidates for interviews
                </p>
              </div>

              <div>
                <div className="text-3xl font-bold mb-1">$4.99</div>
                <p className="text-white/90 text-sm">
                  One-time payment for lifetime verification — no renewals
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl p-10 border-2 border-gray-200 text-center">
          <Shield className="w-16 h-16 mx-auto mb-6 text-cyan-500" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Stand Out?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Join thousands of verified job seekers who get noticed faster and hired sooner.
          </p>
          <button className="px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl text-lg inline-flex items-center gap-2">
            Get Verified for $4.99
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-sm text-gray-500 mt-4">
            100% money-back guarantee • Instant processing
          </p>
        </div>
      </div>
    </div>
  );
}
